import type { ConnectorCall, ResponseScenario } from "./voice-cao-dock-data";

interface PendingVoiceContext {
  kind: "price" | "chart" | "source" | "task";
  label: string;
  detail: string;
}

export function buildPendingVoiceResponse({
  prompt,
  context,
}: {
  prompt: string;
  context: PendingVoiceContext | null;
}): ResponseScenario {
  const phrase = resolvePendingPhrase(prompt, context);
  const promptLine = summarizePrompt(prompt);
  const contextLine = context
    ? `${context.label}${context.detail ? ` - ${context.detail}` : ""}`
    : "Voice request";

  return {
    kind: "open-chat",
    phrase,
    stream: `${phrase}. ${promptLine}`,
    tools: context ? [toolForContext(context)] : [],
    openChat: {
      label: "",
      detail: "Working from the voice request.",
      href: "#open-chat",
    },
    textRows: [
      promptLine,
      contextLine,
      "Harper is checking the request before answering.",
      "No broker action is taken from voice.",
    ],
  };
}

function resolvePendingPhrase(
  prompt: string,
  context: PendingVoiceContext | null,
): string {
  const text = prompt.toLowerCase();
  if (context?.kind === "chart" || context?.kind === "price") {
    return "Checking market context";
  }
  if (/\b(chart|level|trade|entry|stop|target|nq|es|spy|qqq)\b/.test(text)) {
    return "Checking market context";
  }
  if (/\b(risk|headline|fed|cpi|rates|auction|vol|vix)\b/.test(text)) {
    return "Checking risk context";
  }
  if (/\b(memo|report|summary|brief|table|note)\b/.test(text)) {
    return "Preparing voice brief";
  }
  return "Working on your request";
}

function summarizePrompt(prompt: string): string {
  const trimmed = prompt.trim().replace(/\s+/g, " ");
  if (trimmed.length <= 96) return trimmed;
  return `${trimmed.slice(0, 93).trim()}...`;
}

function toolForContext(context: PendingVoiceContext): ConnectorCall {
  if (context.kind === "chart" || context.kind === "price") {
    return {
      icon: "consul",
      name: "QuickScout",
      action: context.label,
    };
  }
  if (context.kind === "source") {
    return {
      icon: "openui",
      name: "OpenUI",
      action: context.label,
    };
  }
  return {
    icon: "riskflow",
    name: "RiskFlow",
    action: context.label,
  };
}
